// stores/counter.js
import { defineStore } from 'pinia'
import { ref, reactive, computed } from "vue";
import uuid from './mix/uuid'
import { getTerritorioKey } from './territorioKey'

export const useTerritoriosStore = defineStore('territorios', () => {
  const list = ref([])
  const puntoEncuentro = ref([])
  const congregacion = ref(null)
  const grupos = ref([])
  // Catálogo de zonas (otras congregaciones); un grupo pertenece a una zona vía zonaId.
  const zonas = ref([])
  const asignados = ref([])
  // Asignaciones vigentes (sin fecha de devolución). Las devueltas se conservan como historial.
  const asignadosActivos = computed(() => asignados.value.filter((a) => !a.returnedAt))
  const activeId = ref(null)
  const setActive = (id) => {
    activeId.value = id
  }

  const setCongregacion = (data) => {
    congregacion.value = data
  }

  const addTerritorio = (data) => {
    list.value = [...list.value, data]
  }

  const update = async (data) => {
    // console.log("update", data)
    if (!data.uuid) {
      data.uuid = await uuid.new()
      list.value = [...list.value, data]
      return
    }
    let nList = list.value
    let i = nList.findIndex(item => item.uuid == data.uuid);

    // El uuid ya existe pero el territorio aún no está en la lista (nuevo recién creado)
    if (i === -1) {
      list.value = [...nList, data]
      return
    }

    list.value =  [...nList.slice(0,i ), data, ...nList.slice(i + 1)]

    // territorios.$patch({list: [...territorios.list, ]})
  }

  // Defensivo: garantiza que todo territorio en la lista tenga uuid (estado heredado/stale)
  const ensureUuids = async () => {
    let changed = false
    for (const item of list.value) {
      if (!item.uuid) {
        item.uuid = await uuid.new()
        changed = true
      }
    }
    if (changed) list.value = [...list.value]
  }
  
  const state = reactive({
    filteredZones: computed(() => {
      const zonas = list.value.map(item => item.zona).filter(item => item !== '' && item !== undefined);
      return zonas.filter((zona, index) => zonas.indexOf(zona) === index);
    }),
    totalManzanas: computed(() => {
      return list.value.reduce((acc, item) => acc + item.mzNumbers.length - 1, 0)
    }),
    filteredTerr: computed(() =>
      listWithName.value.map(item => ({
        ...item,
        name: `${item.territorio_nombre} - ${item.referencia}`
      }))
    ),
    orderByMzNumbers: computed(() =>
      [...list.value].sort((a, b) => a.mzNumbers[0] - b.mzNumbers[0])
    ),
  });

  const byKey = computed(() => {
    const obj = {}
    listWithName.value.forEach(item => {
      obj[getTerritorioKey(item.zona, item.numero)] = item
      // Alias por el nombre visible: zonas guardadas en minúscula/mixta
      // (ej. "Sur12") no coinciden con la clave canónica uppercase ("SUR12"),
      // y los lookups por territorio_nombre fallaban para esas zonas.
      obj[item.territorio_nombre] = item
    })
    return obj
  })

  const byUuid = computed(() => {
    const obj = {}
    listWithName.value.forEach(item => {
      if (item.uuid) obj[item.uuid] = item
    })
    return obj
  })

  // Alias de retrocompatibilidad — mismo computed indexado por clave uppercase
  const data = byKey
  const all = byKey

  const addPuntoEncuentroToTerritorio = (key, punto) => {
    const item = byKey.value[key] // resuelve uuid
    if (!item) return null
    const raw = list.value.find(t => t.uuid === item.uuid) // objeto crudo de list
    if (!raw) return null
    const updated = { ...raw, puntoEncuentro: [...(raw.puntoEncuentro || []), punto] }
    list.value = list.value.map(t => t.uuid === updated.uuid ? updated : t)
    return updated
  }

  const getTerritorio = (zona, numero) => byKey.value[getTerritorioKey(zona, numero)]

  const getTerritorioById = (uuid) => byUuid.value[uuid]

  const getNumberWithZeros = (number, length) => {
    return number.toString().padStart(length, "0")
  }

  const listWithName = computed(() => {
    let data = list.value.map(item => {
      const zona = item.zona || ""
    
      return {
        ...item,
        territorio_nombre: `${zona}${item.numero}`,
        territorio_nombre_order: `${zona}${getNumberWithZeros(item.numero, 5)}`
      }
    })
    // order data by territorio_nombre asc, a-z
    return data.sort((a, b) => {
      return a.territorio_nombre_order.localeCompare(b.territorio_nombre_order)
    })
  });

  return {
    list,
    listWithName,
    byKey,
    byUuid,
    data,
    all,
    activeId,
    grupos,
    zonas,
    asignados,
    asignadosActivos,
    congregacion,
    puntoEncuentro,
    addTerritorio,
    setActive,
    update,
    ensureUuids,
    getTerritorio,
    addPuntoEncuentroToTerritorio,
    state,
    getTerritorioById,
    setCongregacion,
  }
})
